import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { DropDownList, type DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Toolbar } from '../components/toolbar/toolbar';
import { ToolbarTitle } from '../components/toolbar/toolbar-title';
import { TextBoxFilter } from '../components/input/textbox-filter';
import { SamplingJobDialog } from '../dialog/sampling-job-dialog';
import { type CheckboxChangeEvent, type TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import type { SamplingJob, SamplingJobStatusRef } from '../services/api.types.ts';
import type { KanbanTaskCardProps } from '../components/kanban/kanban-task-card.types.ts';
import { formatDate } from '@progress/kendo-intl';
import { type CalendarChangeEvent } from '@progress/kendo-react-dateinputs';
import { PlanningResolution } from './sampling-board-planning-page.types.ts';
import { ErrorRender } from '../components/render/error-render.tsx';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { KanbanBoard } from '../components/kanban/kanban-board.tsx';
import { ToolbarSeparator } from '../components/toolbar/toolbar-separator.tsx';
import { getSamplingJobs, getSamplingJobStatuses, updateSamplingJob } from '../services/api.ts';
import { getWeekEnd, getWeekOffset, getWeekRange, getWeekStart } from '../utils/date.ts';
import { Checkbox } from '../components/input/checkbox.tsx';
import { SamplingPlanningTaskCard } from '../content/kanban/sampling-planning-task-card.tsx';
import { SamplingDateToolbarCtrl } from '../content/toolbar/sampling-date-toolbar-ctrl.tsx';
import { UnassignedJobsDrawer } from '../content/layout/unassigned-jobs-drawer.tsx';
import { DndLayout } from '../components/layout/dnd-layout.tsx';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { KanbanColumn } from '../components/kanban/kanban-column.tsx';
import { Button } from '@progress/kendo-react-buttons';
import { LayoutDrawerPosition } from '../components/layout/layout-drawer.types.ts';
import styles from './sampling-board-planning-page.module.scss';

const DAYS_IN_WEEK = [0, 1, 2, 3, 4, 5, 6];
const BOARD_WEEK_OFFSETS: number[] = [0, 1, 2, 3, 4];
const RESOLUTION_OPTIONS = [
  { id: PlanningResolution.Weekly, title: 'Weekly Board' },
  { id: PlanningResolution.Daily, title: 'Daily Board' },
];

const BREADCRUMB_ITEMS = [
  {
    id: 'homepage',
    text: 'Home',
    url: '/',
    icon: <FontAwesomeIcon icon={faHome} size={'lg'} />,
  },
  { id: 'sampling-board', text: 'Sampling Board' },
  { id: 'sampling-board-planning', text: 'Planning' },
];

export function SamplingBoardPlanningPage() {
  const [samplingJobStatuses, setSamplingJobStatuses] = useState<SamplingJobStatusRef[]>([]);
  const [samplingJobs, setSamplingJobs] = useState<SamplingJob[]>([]);
  const [openSamplingJob, setOpenSamplingJob] = useState<SamplingJob | null>(null);
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [weekCtrlDate, setWeekCtrlDate] = useState<Date>(new Date());
  const [boardResolution, setBoardResolution] = useState<PlanningResolution>(PlanningResolution.Weekly);
  const [inactiveFlag, setInactiveFlag] = useState<boolean>(false);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<number> | null>(null);
  const [activeDragContext, setActiveDragContext] = useState<SamplingJob | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [textFilter, setTextFilter] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const activeWeekRange = useMemo(() => {
    const now = new Date();
    if (boardResolution === PlanningResolution.Weekly) {
      return {
        start: getWeekStart(now, weekOffset + BOARD_WEEK_OFFSETS[0]),
        end: getWeekEnd(now, weekOffset + BOARD_WEEK_OFFSETS[BOARD_WEEK_OFFSETS.length - 1]),
      };
    }

    return {
      start: getWeekStart(now, weekOffset),
      end: getWeekEnd(now, weekOffset),
    };
  }, [weekOffset, boardResolution]);

  const filteredBoardTasks = useMemo(() => {
    const search = textFilter.trim().toLowerCase();

    return samplingJobs.reduce<SamplingJob[]>((acc, job) => {
      const matchesSearch =
        !search ||
        `ssj${String(job.id)}`.includes(search) ||
        job.companyTitle.toLowerCase().includes(search) ||
        job.clientTitle.toLowerCase().includes(search) ||
        job.clientRef?.toLowerCase().includes(search) ||
        job.orderNumber?.toLowerCase().includes(search);

      if (matchesSearch) {
        acc.push(job);
      }

      return acc;
    }, []);
  }, [textFilter, samplingJobs]);

  const assignedBoardTasks = useMemo(() => {
    return filteredBoardTasks.filter((job) => job.plannedDate);
  }, [filteredBoardTasks]);

  const unassignedBoardTasks = useMemo(() => {
    return filteredBoardTasks
      .filter((job) => !job.plannedDate)
      .sort((a, b) => {
        return b.id - a.id;
      });
  }, [filteredBoardTasks]);

  const samplingBoardColumns = useMemo(() => {
    if (boardResolution === PlanningResolution.Weekly) {
      return BOARD_WEEK_OFFSETS.map((offset) => {
        const week = getWeekRange(weekOffset + offset);
        return {
          id: week.offset,
          title: `${formatDate(week.start, 'd MMM yy')} - ${formatDate(week.end, 'd MMM yy')}`,
          data: assignedBoardTasks
            .filter((job) => getWeekStart(new Date(job.plannedDate!)).getTime() === week.start.getTime())
            .sort((a, b) => {
              return b.id - a.id;
            }),
        };
      });
    }

    const currentWeekStart = getWeekStart(new Date(), weekOffset);
    return DAYS_IN_WEEK.map((dayIndex) => {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + dayIndex);

      return {
        id: dayIndex,
        title: formatDate(date, 'EEE, d MMM yy'),
        data: assignedBoardTasks
          .filter((job) => new Date(job.plannedDate!).getTime() === date.getTime())
          .sort((a, b) => {
            return b.id - a.id;
          }),
      };
    });
  }, [assignedBoardTasks, boardResolution, weekOffset]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const statuses = await getSamplingJobStatuses();
        if (isMounted) {
          setSamplingJobStatuses(statuses);
        }
      } catch (error) {
        console.error('Error loading sampling board:', error);
        if (isMounted) {
          setError('Unable to load sampling board');
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      try {
        const jobs = await getSamplingJobs({
          showInactive: inactiveFlag,
          startDate: activeWeekRange.start,
          endDate: activeWeekRange.end,
        });
        if (isMounted) {
          setSamplingJobs(jobs);
        }
      } catch (error) {
        console.error('Error loading sampling board:', error);
        if (isMounted) {
          setError('Unable to load sampling board');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [inactiveFlag, activeWeekRange]);

  const renderDrawer = useCallback(
    (position: LayoutDrawerPosition) => {
      return (
        <UnassignedJobsDrawer
          data={unassignedBoardTasks}
          position={position}
          width={'15%'}
          open={drawerOpen}
          onOpen={() => setDrawerOpen((prev) => !prev)}
          onOpenDialog={({ job }) => setOpenSamplingJob(job)}
        />
      );
    },
    [drawerOpen, unassignedBoardTasks]
  );

  const renderAssignedTaskCard = useCallback((props: KanbanTaskCardProps) => {
    return (
      <SamplingPlanningTaskCard
        {...props}
        data={props.data as unknown as SamplingJob}
        onOpenDialog={({ job }) => setOpenSamplingJob(job)}
      />
    );
  }, []);

  const renderDndOverlay = useCallback(() => {
    return <SamplingPlanningTaskCard data={activeDragContext!} isOverlay={true} />;
  }, [activeDragContext]);

  const handleResolutionChange = ({ value }: DropDownListChangeEvent) => {
    setBoardResolution(value.id as PlanningResolution);
    if (value.id === PlanningResolution.Weekly) {
      setHiddenColumnIds(null);
    } else {
      setHiddenColumnIds(new Set([5, 6]));
    }
  };

  const handleTextFilterChange = ({ value }: TextBoxChangeEvent) => {
    setTextFilter(String(value));
  };

  const handleTextFilterClear = () => {
    setTextFilter('');
  };

  const handlePrevWeek = () => {
    setWeekOffset((prev) => prev - 1);
  };

  const handleNextWeek = () => {
    setWeekOffset((prev) => prev + 1);
  };

  const handleDateChange = ({ value }: CalendarChangeEvent) => {
    const offset = getWeekOffset(value);
    setWeekOffset(offset);
    setWeekCtrlDate(value);
  };

  const handleFlagChange = ({ value }: CheckboxChangeEvent) => {
    setInactiveFlag(value);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const jobs = await getSamplingJobs({
        showInactive: inactiveFlag,
        startDate: activeWeekRange.start,
        endDate: activeWeekRange.end,
      });
      setSamplingJobs(jobs);
    } catch (error) {
      console.error('Error loading sampling board:', error);
      setError('Unable to load sampling board');
    } finally {
      setLoading(false);
    }
  };

  const handleKanbanColumnToggleView = (columnId: number) => {
    setHiddenColumnIds((prev) => {
      const next = new Set(prev);

      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }

      return next;
    });
  };

  const handleDragStart = (e: DragStartEvent) => {
    setActiveDragContext(e.active.data.current as SamplingJob);
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveDragContext(null);

    const columnId = over?.id;
    if (columnId == null) {
      return;
    }

    const updatedJob = active?.data?.current as SamplingJob;
    if (updatedJob == null) {
      return;
    }

    const prevJob = { ...updatedJob };

    if (boardResolution === PlanningResolution.Weekly) {
      updatedJob.plannedDate = getWeekStart(new Date(), Number(columnId)).toISOString();
    } else {
      const currentWeekStart = getWeekStart(new Date(), weekOffset);
      const targetDate = new Date(currentWeekStart);
      targetDate.setDate(currentWeekStart.getDate() + Number(columnId));
      updatedJob.plannedDate = targetDate.toISOString();
    }

    setSamplingJobs((prev) => prev.map((job) => (job.id === updatedJob.id ? updatedJob : job)));

    try {
      await updateSamplingJob({
        jobId: updatedJob.id,
        plannedDate: updatedJob.plannedDate,
        estimatedDays: updatedJob.estimatedDays,
        statusId: updatedJob.statusId,
        active: updatedJob.active,
      });
    } catch (error) {
      console.error('Failed to update kanban board column:', error);
      setSamplingJobs((prev) => prev.map((job) => (job.id === prevJob.id ? prevJob : job)));
    }
  };

  const handleDragCancel = () => {
    setActiveDragContext(null);
  };

  const handleDialogApply = async () => {
    try {
      const jobs = await getSamplingJobs({
        showInactive: inactiveFlag,
        startDate: activeWeekRange.start,
        endDate: activeWeekRange.end,
      });
      setSamplingJobs(jobs);
    } catch (error) {
      console.error('Error loading sampling board:', error);
      setError('Unable to load sampling board');
    } finally {
      handleDialogClose();
    }
  };

  const handleDialogClose = () => {
    setOpenSamplingJob(null);
  };

  return (
    <>
      <DndLayout
        breadcrumb={BREADCRUMB_ITEMS}
        renderDrawer={{ left: renderDrawer }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        renderOverlay={renderDndOverlay}
      >
        <Toolbar>
          <ToolbarTitle flex={1}>Sampling Planning Board</ToolbarTitle>
          <ToolbarSeparator />
          <DropDownList
            data={RESOLUTION_OPTIONS}
            dataItemKey={'id'}
            textField={'title'}
            value={RESOLUTION_OPTIONS.find((opt) => opt.id === boardResolution)}
            onChange={handleResolutionChange}
            style={{ width: 200 }}
          />
          <ToolbarSeparator />
          <TextBoxFilter
            value={textFilter}
            onChange={handleTextFilterChange}
            onClear={handleTextFilterClear}
            style={{ width: 280 }}
            placeholder={'Filter by company, client, job, order no..'}
          />
          <ToolbarSeparator />
          <SamplingDateToolbarCtrl
            selectedDate={weekCtrlDate}
            weekStart={activeWeekRange.start}
            weekEnd={activeWeekRange.end}
            onPrevWeek={handlePrevWeek}
            onNextWeek={handleNextWeek}
            onChange={handleDateChange}
          />
          <ToolbarSeparator />
          <Checkbox checked={inactiveFlag} label={'Show Inactive'} onChange={handleFlagChange} />
          <ToolbarSeparator />
          <Button themeColor={'primary'} className={styles.refreshBtn} onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} size={'1x'} />
          </Button>
        </Toolbar>
        {loading ? (
          <LoaderRender message={'Loading...'} />
        ) : error.length > 0 ? (
          <ErrorRender message={error} />
        ) : (
          <KanbanBoard className={styles.kanbanBoard}>
            {samplingBoardColumns.map((column) => (
              <KanbanColumn
                {...column}
                key={column.id}
                data={column.data}
                collapsed={hiddenColumnIds?.has(column.id)}
                renderCard={renderAssignedTaskCard}
                onToggleView={handleKanbanColumnToggleView}
              />
            ))}
          </KanbanBoard>
        )}
      </DndLayout>
      {openSamplingJob !== null && (
        <SamplingJobDialog
          job={openSamplingJob}
          jobStatuses={samplingJobStatuses}
          onApply={handleDialogApply}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
}
